import { useCallback, useEffect } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { withRecommendationProvider } from 'libs/searchspring/components/recommendations/withRecommendationsProvider'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'
import { calculateProductCount } from 'libs/searchspring/components/collection/filters/ProductCount'

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []
const popularProductsProfile = globalState.search?.popularProductsProfile || 'home'
const popularProductsTitle = globalState.search?.popularProductsTitle
const popularProductsCtaText = globalState.search?.popularProductsCtaText
const popularProductsCtaLink = globalState.search?.popularProductsCtaLink

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Collections', type: 'collections', default: collectionsSuggestions, isStatic: true },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: popularProductsTitle
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches' },
      { title: 'Popular Collections', type: 'collections', default: collectionsSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches' },
      { title: 'Popular Collections', type: 'collections', default: collectionsSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * ViewResultsButton component - Renders the "View Results" button for search results
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {number} props.totalResults - Total number of search results
 * @returns {JSX.Element} Rendered component
 */
export const ViewResultsButton = ({ query, totalResults }) => {
  if (!query || !totalResults) return null

  return (
    <SearchPageLink query={query} className="shrink-0 btn btn-primary btn-fill btn-m w-fit">
      View {totalResults} Results
    </SearchPageLink>
  )
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect, onResultsUpdate }) {
  const { refine, results, isLoading } = useSearchspringContext()
  const { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady } = useSearchResultState({ query, results, isLoading })
  const Recommendations = useCallback(withRecommendationProvider(CarouselResults, 'recommend'), [])

  const itemClass = 'w-3/5 lg:w-full max-w-[254px] md:max-w-[calc(100%/3.2)] [_&>div]:w-full'
  const productCardSettings = {
    hideQuickAdd: false,
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: false,
    hideWishlist: false
  }
  const carouselSettings = {
    draggable: true,
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    fade: false,
    adaptiveHeight: false,
    accessibility: true,
    cellAlign: 'left',
    prevNextButtons: true,
    pageDots: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    if (onResultsUpdate && results) {
      const totalResults = calculateProductCount(results)
      const hasResults = totalResults > 0
      onResultsUpdate({ hasResults, totalResults })
    }
  }, [results, onResultsUpdate])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    let config
    if (query && hasResults) {
      config = SEARCH_CONFIG.HAS_RESULTS
    } else if (query && !hasResults) {
      config = SEARCH_CONFIG.NO_RESULTS
    } else {
      config = SEARCH_CONFIG.NO_SEARCH
    }
    return config.suggestions.map((suggestion, index) => (
      <SearchSuggestions
        key={index}
        title={suggestion.title}
        type={suggestion.type}
        query={query && !hasResults ? '' : query}
        defaultSuggestions={suggestion.default}
        isStatic={suggestion.isStatic}
      />
    ))
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or Recommendations component
   */
  return (
    <div className="grid lg:grid-cols-main lg:auto-rows-auto max-h-[75vh] bg-secondary overflow-y-scroll no-scrollbar overscroll-none">
      {/* Main Content Section */}
      <div className="shrink-0 lg:col-[4/-1] lg:row-[2/3] flex flex-col gap-y-1.5 w-full h-fit lg:h-full lg-max:border-b lg:border-l border-neutral-medium overflow-x-hidden px-margin">
        <div className="flex lg-max:flex-col gap-1.25 lg:items-center lg:justify-between pt-1.5">
          <h2 className="h5 font-semibold">{title}</h2>
          {/* "View results" button */}
          {(hasResults && !redirect) ? (
            <ViewResultsButton query={query} totalResults={calculateProductCount(results)} />
          ) : (
            popularProductsCtaText && popularProductsCtaLink ? (
              <a href={popularProductsCtaLink} className="btn btn-primary btn-fill btn-m w-fit" aria-label={popularProductsCtaText}>
                {popularProductsCtaText}
              </a>
            ) : null
          )}
        </div>

        <div
          className="[&_.flickity-button-icon]:fill-[--carousel-nav-icon-colour] [&_.flickity-button]:bg-[--carousel-nav-bg-colour] flickity-container-full"
          style="
            --padding-left: var(--padding-margin);
            --padding-right: var(--padding-margin);
            --padding-left-mb: var(--padding-margin);
            --padding-right-mb: var(--padding-margin);
            --carousel-nav-bg-colour: var(--black);
            --carousel-nav-icon-colour: var(--white);
          "
        >
          {/* Loading Overlay */}
          <div className={clsx('w-full', (isCheckingResults || isLoading) ? '' : 'h-0 opacity-0 pointer-events-none')}>
            <SkeletonGrid carouselSettings={carouselSettings} itemClass={itemClass} />
          </div>
          <div className={clsx('w-full', (hasResults) ? '' : 'hidden')}>
            {isResultsReady && (
              <QuickSearchResults />
            )}
          </div>
          <div className={clsx('w-full', (!hasResults && query && !isLoading && showNoResults) ? '' : 'hidden')} data-dl-section-id="home">
            {isRecommendationsReady && (
              <Recommendations
                profile={popularProductsProfile}
                limit={4}
                itemClass={itemClass}
                productCardSettings={{ ...productCardSettings, hideRange: true }}
                carouselSettings={carouselSettings}
              />
            )}
          </div>
          <div className={clsx('w-full', query ? 'hidden' : '')}>
            <Recommendations profile={popularProductsProfile} limit={4} itemClass={itemClass} productCardSettings={{ ...productCardSettings, hideRange: true }} carouselSettings={carouselSettings} />
          </div>
        </div>
      </div>

      {/* Suggestions Section */}
      <div className="shrink-0 lg:col-[1/4] lg:row-[2/3] flex flex-col gap-y-1.5 lg:gap-y-2.5 w-full h-fit lg:h-full py-1.5 lg:py-2.5">
        {renderSuggestions()}
      </div>

      {/* No Results Section */}
      {(!hasResults && query && !isLoading && showNoResults) && (
        <div className="shrink-0 lg:col-span-full row-[1] flex flex-col w-full px-margin py-1 lg:py-1.875 bg-system-error-light border-b border-neutral-medium">
          <span className="h6"><span className="font-semibold">Sorry, no results for "{query}".</span> We recommend checking the spelling or try another search term.</span>
        </div>
      )}
    </div>
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {Object} props.carouselSettings - Settings for the carousel
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ carouselSettings, itemClass }) => {
  return (
    <x-carousel
      className="flickity-container flex h-fit"
      carousel-settings={JSON.stringify(carouselSettings)}
    >
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
    </x-carousel>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'pages')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, isStatic = false }) => {
  return (
    <div className="flex flex-col gap-y-1.5 border-b border-neutral-medium px-margin pb-1.5 lg:pb-2.5 last:border-b-0 last:pb-0">
      <h3 className="paragraph-s font-semibold uppercase">{title}</h3>
      {isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : (
        query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
        ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
        )
      )}
    </div>
  )
}
